import React, { useState } from 'react'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { useQueryClient } from 'react-query'
import { Clock, MessageSquare, Activity, Reply, ChevronDown, ChevronUp, Pencil, Trash2, Bot } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { taskService } from '@/services/taskService'
import { timeEntryService } from '@/services/timeEntryService'
import RichTextEditor from './RichTextEditor'
import EditTimeEntryForm from './EditTimeEntryForm'

const getActivityLabel = (activity) => {
  switch (activity.action) {
    case 'task_created': return 'created this task'
    case 'status_changed': return `changed status from "${activity.old_value}" → "${activity.new_value}"`
    case 'assignee_changed':
      if (!activity.old_value) return `assigned to ${activity.new_value || 'someone'}`
      if (!activity.new_value) return `removed assignee ${activity.old_value}`
      return `changed assignee from ${activity.old_value} → ${activity.new_value}`
    case 'priority_changed': return `changed priority from "${activity.old_value}" → "${activity.new_value}"`
    case 'due_date_changed':
      if (!activity.old_value) return `set due date to ${activity.new_value}`
      if (!activity.new_value) return `removed due date`
      return `changed due date from ${activity.old_value} → ${activity.new_value}`
    case 'comment_added': return 'commented'
    case 'attachment_added': return `attached "${activity.new_value}"`
    case 'attachment_removed': return `removed attachment "${activity.old_value}"`
    case 'type_changed': return `changed type from "${activity.old_value}" → "${activity.new_value}"`
    case 'title_changed': return `renamed the task from "${activity.old_value}" → "${activity.new_value}"`
    case 'description_changed': return 'updated the description'
    case 'acceptance_criteria_changed': return 'updated the acceptance criteria'
    case 'parent_changed':
      if (!activity.old_value) return `set the parent to ${activity.new_value}`
      if (!activity.new_value) return `removed the parent ${activity.old_value}`
      return `moved from ${activity.old_value} → ${activity.new_value}`
    case 'labels_changed': return `changed labels from "${activity.old_value || 'none'}" → "${activity.new_value || 'none'}"`
    case 'link_added': return `linked: ${activity.new_value}`
    case 'link_removed': return `removed link: ${activity.old_value}`
    case 'comment_edited': return 'edited a comment'
    case 'comment_deleted': return `deleted a comment${activity.old_value ? `: "${activity.old_value}"` : ''}`
    case 'time_entry_updated': return `edited a time entry (${activity.old_value} → ${activity.new_value})`
    case 'time_entry_deleted': return `deleted a time entry (${activity.old_value})`
    case 'sprint_changed': return `moved from ${activity.old_value || 'Backlog'} → ${activity.new_value || 'Backlog'}`
    case 'story_points_changed': return `changed story points from ${activity.old_value ?? 'none'} → ${activity.new_value ?? 'none'}`
    case 'estimate_changed': return `changed the estimate from ${activity.old_value || 'none'} → ${activity.new_value || 'none'}`
    case 'time_logged': return `logged ${activity.new_value}`
    default: return activity.action.replace(/_/g, ' ')
  }
}

// Marks work an AI agent did on someone's behalf through an API token (e.g. Claude Code)
const ViaBadge = ({ name }) => {
  if (!name) return null
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded bg-violet-100 px-1.5 text-[10px] font-medium text-violet-700 dark:bg-violet-950 dark:text-violet-300"
      title={`Done through the API token "${name}"`}
    >
      <Bot className="h-3 w-3" /> via {name}
    </span>
  )
}

const getInitial = (user) => {
  if (!user) return '?'
  if (user.employee?.first_name) return user.employee.first_name.charAt(0).toUpperCase()
  return user.email?.charAt(0).toUpperCase() || '?'
}

const getName = (user) => {
  if (!user) return 'Unknown'
  if (user.employee?.first_name) return `${user.employee.first_name} ${user.employee.last_name}`
  return user.email
}

// Single comment with reply support
const CommentItem = ({ comment, projectId, taskId, isReply = false, mentionUsers = [], currentUserId }) => {
  const queryClient = useQueryClient()
  const [showReplyEditor, setShowReplyEditor] = useState(false)
  const [showReplies, setShowReplies] = useState(true)
  const [replyContent, setReplyContent] = useState('')
  const [replyLoading, setReplyLoading] = useState(false)
  const [editorKey, setEditorKey] = useState(0)
  const [editing, setEditing] = useState(false)
  const [editContent, setEditContent] = useState('')
  const [editLoading, setEditLoading] = useState(false)
  const [editEditorKey, setEditEditorKey] = useState(100)

  const user = comment.author
  const initial = getInitial(user)
  const name = getName(user)
  const dateStr = format(new Date(comment.created_at), 'MMM d, h:mm a')
  const replies = comment.replies || []
  const isOwn = user?.id === currentUserId

  const handleReply = async () => {
    const text = replyContent.replace(/<[^>]*>/g, '').trim()
    if (!text) return toast.error('Reply cannot be empty')

    setReplyLoading(true)
    try {
      await taskService.addComment(projectId, taskId, replyContent, comment.id)
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      setReplyContent('')
      setEditorKey(k => k + 1)
      setShowReplyEditor(false)
      toast.success('Reply added')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reply')
    } finally {
      setReplyLoading(false)
    }
  }

  const handleEdit = () => {
    setEditContent(comment.content)
    setEditing(true)
  }

  const handleEditSave = async () => {
    const text = editContent.replace(/<[^>]*>/g, '').trim()
    if (!text) return toast.error('Comment cannot be empty')

    setEditLoading(true)
    try {
      await taskService.updateComment(projectId, taskId, comment.id, editContent)
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      setEditing(false)
      toast.success('Comment updated')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update comment')
    } finally {
      setEditLoading(false)
    }
  }

  const handleEditCancel = () => {
    setEditing(false)
    setEditEditorKey(k => k + 1)
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return
    try {
      await taskService.deleteComment(projectId, taskId, comment.id)
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      toast.success('Comment deleted')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete comment')
    }
  }

  return (
    <div className={isReply ? 'ml-9 mt-2' : ''}>
      <div className="flex gap-2.5">
        <Avatar className="h-7 w-7 shrink-0 mt-0.5">
          <AvatarFallback className="text-[10px] bg-primary/10 text-primary">{initial}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium">{name}</span>
            <span className="text-xs text-muted-foreground">{dateStr}</span>
            <ViaBadge name={comment.apiToken?.name} />
            {comment.updated_at && comment.updated_at !== comment.created_at && (
              <span className="text-xs text-muted-foreground italic">(edited)</span>
            )}
          </div>
          {editing ? (
            <div className="mt-1 space-y-2">
              <RichTextEditor
                key={editEditorKey}
                content={editContent}
                onChange={setEditContent}
                placeholder="Edit comment..."
                minHeight={60}
                mentionUsers={mentionUsers}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={handleEditCancel} disabled={editLoading}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleEditSave} disabled={editLoading}>
                  {editLoading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="mt-1 text-sm prose prose-sm max-w-none rounded-md bg-muted/50 px-3 py-2 border"
              dangerouslySetInnerHTML={{ __html: comment.content }}
            />
          )}
          {/* Action buttons — Reply / Edit / Delete */}
          {!editing && (
            <div className="flex items-center gap-1 mt-1">
              {!isReply && (
                <Button
                  variant="ghost" size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowReplyEditor(!showReplyEditor)}
                >
                  <Reply className="h-3 w-3 mr-1" />
                  Reply
                </Button>
              )}
              {isOwn && (
                <>
                  <Button
                    variant="ghost" size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={handleEdit}
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={handleDelete}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </>
              )}
              {!isReply && replies.length > 0 && (
                <Button
                  variant="ghost" size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowReplies(!showReplies)}
                >
                  {showReplies ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                  {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Replies */}
      {!isReply && showReplies && replies.length > 0 && (
        <div className="space-y-2 mt-1">
          {replies
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
            .map(reply => (
              <CommentItem
                key={reply.id}
                comment={reply}
                projectId={projectId}
                taskId={taskId}
                isReply
                mentionUsers={mentionUsers}
                currentUserId={currentUserId}
              />
            ))}
        </div>
      )}

      {/* Reply editor */}
      {showReplyEditor && (
        <div className="ml-9 mt-2 space-y-2">
          <RichTextEditor
            key={editorKey}
            content=""
            onChange={setReplyContent}
            placeholder="Write a reply..."
            minHeight={60}
            mentionUsers={mentionUsers}
          />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowReplyEditor(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleReply} disabled={replyLoading}>
              {replyLoading ? 'Posting...' : 'Reply'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

const TaskActivityTimeline = ({ activities = [], comments = [], timeEntries = [], projectId, taskId, filter = 'all', mentionUsers = [], currentUserId }) => {
  const queryClient = useQueryClient()
  const [editingTimeEntryId, setEditingTimeEntryId] = useState(null)

  // Merge all three sources
  let items = [
    ...activities.map(a => ({ ...a, _type: 'activity', _date: new Date(a.created_at) })),
    ...comments.map(c => ({ ...c, _type: 'comment', _date: new Date(c.created_at) })),
    ...timeEntries.filter(t => t.end_time).map(t => ({ ...t, _type: 'time', _date: new Date(t.created_at) })),
  ].sort((a, b) => b._date - a._date)

  // Apply filter
  if (filter === 'comments') items = items.filter(i => i._type === 'comment')
  if (filter === 'history') items = items.filter(i => i._type === 'activity')
  if (filter === 'time') items = items.filter(i => i._type === 'time')

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
  }

  const handleDeleteTimeEntry = async (entryId) => {
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return
    try {
      await timeEntryService.delete(entryId)
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      toast.success('Time entry deleted')
    } catch {
      toast.error('Failed to delete time entry')
    }
  }

  return (
    <div className="space-y-3">
      {items.map((item, i) => {
        if (item._type === 'comment') {
          return (
            <CommentItem
              key={`comment-${item.id}`}
              comment={item}
              projectId={projectId}
              taskId={taskId}
              mentionUsers={mentionUsers}
              currentUserId={currentUserId}
            />
          )
        }

        const user = item._type === 'time' ? item.user : item.actor
        const initial = getInitial(user)
        const name = getName(user)
        const dateStr = format(item._date, 'MMM d, h:mm a')

        if (item._type === 'time') {
          const isOwn = item.user_id === currentUserId

          if (editingTimeEntryId === item.id) {
            return (
              <EditTimeEntryForm
                key={`time-${item.id}`}
                entry={item}
                taskId={taskId}
                projectId={projectId}
                onClose={() => setEditingTimeEntryId(null)}
              />
            )
          }

          const mins = Math.floor((item.duration || 0) / 60)
          const h = Math.floor(mins / 60)
          const m = mins % 60
          const dur = h > 0 ? `${h}h ${m}m` : `${m}m`
          return (
            <div key={`time-${item.id}`} className="group flex gap-2.5 items-start">
              <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div className="flex-1 flex items-start justify-between gap-2">
                <span className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{name}</span> logged <span className="font-medium text-foreground">{dur}</span>
                  {item.note && <span> — {item.note}</span>}
                  <span className="ml-2 text-xs">{dateStr}</span>
                  {item.apiToken?.name && <span className="ml-2"><ViaBadge name={item.apiToken.name} /></span>}
                </span>
                {isOwn && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      onClick={() => setEditingTimeEntryId(item.id)}
                      className="p-0.5 rounded hover:bg-muted"
                      title="Edit time entry"
                    >
                      <Pencil className="h-3 w-3 text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => handleDeleteTimeEntry(item.id)}
                      className="p-0.5 rounded hover:bg-destructive/10"
                      title="Delete time entry"
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        }

        // activity
        return (
          <div key={`activity-${item.id}`} className="flex gap-2.5 items-start">
            <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
              <Activity className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div>
              <span className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{name}</span>{' '}
                {getActivityLabel(item)}
                <span className="ml-2 text-xs">{dateStr}</span>
                {item.metadata?.api_token_name && <span className="ml-2"><ViaBadge name={item.metadata.api_token_name} /></span>}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default TaskActivityTimeline
